import { useGetAccountsQuery } from "@/modules/account/services";
import { LoanType } from "@/modules/loan/common/enum";
import type { ILoan } from "@/modules/loan/common/types";
import { useGetLoansQuery } from "@/modules/loan/services";
import {
  LoanRepaymentKeys,
  LoanRepaymentType,
  TransferKeys
} from "@/modules/transaction/common/enum";
import type {
  GetLoanByContractNumber,
  GetLoanPenalties,
  IGetInfoProps
} from "@/modules/transaction/common/types";
import {
  getAccountsFilteredList,
  getLoanRepaymentTypeOptions
} from "@/modules/transaction/helpers";
import { AllowedAccountTransferCurrencies, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import { SearchParamKeys } from "@/shared/common/enums";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

interface IUseLoanRepaymentParam {
  isCopy?: boolean;
  isEdit?: boolean;
  isTemplate?: boolean;
  currency?: string;
  savedAmount?: number | string;
  getLoan: GetLoanByContractNumber;
  getLoanPenalties: GetLoanPenalties;
}

export const useLoanRepayment = ({
  isEdit,
  isCopy,
  isTemplate,
  currency,
  savedAmount,
  getLoan,
  getLoanPenalties,
}: IUseLoanRepaymentParam) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const loanContractNumber = searchParams.get(SearchParamKeys.LoanContractNumber);
  const { setValue, setError, trigger, watch, reset } = useFormProps();
  const isInitialMount = useRef(true);
  const { data: loanList = [] } = useGetLoansQuery({
    pageSize: INITIAL_PAGE_SIZE,
    loanTypes: [LoanType.Standard, LoanType.CreditLine],
  }, {
    refetchOnMountOrArgChange: true,
  });

  const { data: accountsList = [] } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const values = watch?.() || {};

  const getLoanInfo = (loanRepaymentData?: IGetInfoProps) => {
    const values = watch?.();
    const data = loanRepaymentData || values;
    if (data?.loanRepaymentType && data?.loanContractNumber) {
      getLoanPenalties({
        contractNumber: data.loanContractNumber,
        loanRepaymentType: data.loanRepaymentType,
        loanRepaymentAmount: data.amount || null,
      });
    }
  };


  const accountsFilteredList = useMemo(() => {
    if (!currency) return [];
    return getAccountsFilteredList({
      accountsList,
      accountFilters: currency ? [currency] : AllowedAccountTransferCurrencies,
    });
  }, [accountsList, currency]);

  const loanOptions = useMemo(() => {
    return loanList.map((loan, index) => ({
      value: loan.contractNumber,
      label: loan.contractNumber,
      meta: { ...loan, index },
    }));
  }, [loanList]);

  const loanRepaymentTypeOptions = getLoanRepaymentTypeOptions({ t });

  useEffect(() => {
    if (loanContractNumber && loanOptions.length) {
      const exists = loanOptions.some(opt => opt.value === loanContractNumber);
      if (exists) {
        setValue(LoanRepaymentKeys.LoanContractNumber, loanContractNumber, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [loanContractNumber, loanOptions]);

  useEffect(() => {
    if (values.loanRepaymentType && values.loanContractNumber) {
      const isInitial = isInitialMount.current;
      const skipAmountUpdate = isInitial && isEdit && savedAmount;
      const skipClearAmount = isInitial && !!savedAmount;
      isInitialMount.current = false;

      if (!skipClearAmount) {
        if (!isEdit && !isCopy) {
          setValue(TransferKeys.Amount, "");
        } else if (values.loanRepaymentType === LoanRepaymentType.PrincipalRepayment) {
          setValue(TransferKeys.Amount, "");
        }
      }
      getLoan({
        loanContractNumber: values.loanContractNumber,
        loanRepaymentType: values.loanRepaymentType,
      }).unwrap().then((res: ILoan) => {
        if (res.upcomingPaymentAmount && !skipAmountUpdate && !isTemplate &&
          values.loanRepaymentType !== LoanRepaymentType.PrincipalRepayment
        ) {
          setValue(TransferKeys.Amount, res.upcomingPaymentAmount, { shouldValidate: true });
          getLoanInfo();
        }
      });
    }
  }, [values.loanRepaymentType, values.loanContractNumber]);

  return {
    reset,
    watch,
    trigger,
    values,
    setError,
    setValue,
    getLoanInfo,
    loanOptions,
    accountsList,
    loanContractNumber,
    accountsFilteredList,
    loanRepaymentTypeOptions,
  };
};
